import { registerLocaleData } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import localeJa from '@angular/common/locales/ja';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MatNativeDateModule,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import {
  CuepidCoreModule,
  ErrorToastInterceptor,
  GoogleMapService,
  MAINTENANCE_OPTION,
} from '@cuepid/core';
import { EgretModule, LAYOUT_CONFIG, NavigationService } from '@cuepid/egret';
import { CuepidDateAdapter } from '@cuepid/enterprise/core/adapters/cuepid-date.adapter';
import { COOKIE_SERVICE } from '@cuepid/enterprise/core/injection-tokens';
import { LoadIndicatorInterceptor } from '@cuepid/enterprise/core/interceptors/load-indicator.interceptor';
import { environment } from '@cuepid/enterprise/env';
import * as Sentry from '@sentry/angular';
import { Angulartics2Module } from 'angulartics2';
import Cookies from 'js-cookie';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import {
  PerfectScrollbarConfigInterface,
  PerfectScrollbarModule,
  PERFECT_SCROLLBAR_CONFIG,
} from 'ngx-perfect-scrollbar';
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from './app.component';
import { rootRouterConfig } from './app.routing';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { CheckMaintenanceModeInterceptor } from './core/interceptors/check-maintenance-mode.interceptor';
import { EnterpriseNavigationService } from './core/services/enterprise-navigation.service';
import { IndexComponent } from './pages/index/index.component';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';
import { SharedComponentsModule } from './shared/components/shared-components.module';

const layoutConfig = {
  matTheme: 'egret-navy',
  perfectScrollbar: true,
  isMobile: false,
  dir: 'ltr',
  navigationPos: 'side',
  sidebarCompactToggle: false,
  sidebarStyle: 'full',
  sidebarColor: 'slate',
  topbarFixed: false,
  topbarColor: 'white',
  footerFixed: false,
  footerColor: 'slate',
  useBreadcrumb: false,
  breadcrumb: 'simple',
};

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    PerfectScrollbarModule,
    RouterModule.forRoot(rootRouterConfig, { useHash: false }),
    ToastrModule.forRoot(),
    FlexLayoutModule,
    NgxMaterialTimepickerModule.setLocale('ja-JP'),
    EgretModule,
    CuepidCoreModule.forRoot({
      apiBaseUrl: environment.apiURL,
      googleMapApiKey: environment.googleMapApiKey,
    }),
    SharedComponentsModule,
    MatButtonModule,
    MatDialogModule,
    MatNativeDateModule,
    Angulartics2Module.forRoot({ developerMode: !environment.production }),
  ],
  declarations: [AppComponent, IndexComponent, MaintenanceComponent],
  providers: [
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router], useValue: undefined },
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorToastInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadIndicatorInterceptor,
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'ja-JP' },
    { provide: NavigationService, useClass: EnterpriseNavigationService },
    { provide: LAYOUT_CONFIG, useValue: layoutConfig },
    { provide: LOCALE_ID, useValue: 'ja' },
    { provide: DateAdapter, useClass: CuepidDateAdapter },
    { provide: COOKIE_SERVICE, useValue: Cookies },
    { provide: MAINTENANCE_OPTION, useValue: environment.maintenance },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CheckMaintenanceModeInterceptor,
      multi: true,
    },
    GoogleMapService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(trace: Sentry.TraceService) {
    registerLocaleData(localeJa);
  }
}
